export type ReportingMonth = `${number}-${string}`;

const MONTH_PAD = 2;
/** Month segment may be 1–2 digits (legacy writes use e.g. `2026-2` instead of `2026-02`). */
const REPORTING_MONTH_REGEX = /^(\d{4})-(\d{1,2})$/;
export const ROLLOVER_DAY = 25;
export const LOCK_DAY = 25;
const INDOCHINA_TIME_ZONE = 'Asia/Bangkok';

const getTimeZoneYearMonth = (date: Date, timeZone: string): { year: string; month: string } => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(date);

  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;

  if (!year || !month) {
    return {
      year: String(date.getUTCFullYear()),
      month: String(date.getUTCMonth() + 1).padStart(MONTH_PAD, '0'),
    };
  }

  return { year, month };
};

const parseReportingMonthParts = (targetMonth: ReportingMonth): { year: number; monthIndex: number } | null => {
  const match = REPORTING_MONTH_REGEX.exec(targetMonth);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);

  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    return null;
  }

  return { year, monthIndex: month - 1 };
};

/**
 * Normalize a reporting month string to `YYYY-MM` so UI keys (year chart, filters) match Firestore
 * documents that store `YYYY-M` for January–September.
 */
export const canonicalReportingMonth = (raw: unknown, fallback: ReportingMonth): ReportingMonth => {
  const coerce = (value: string): ReportingMonth | null => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parts = parseReportingMonthParts(trimmed as ReportingMonth);
    if (!parts) return null;
    return `${parts.year}-${String(parts.monthIndex + 1).padStart(MONTH_PAD, '0')}` as ReportingMonth;
  };
  if (typeof raw === 'string') {
    const fromRaw = coerce(raw);
    if (fromRaw) return fromRaw;
  }
  const fromFallback = coerce(String(fallback));
  return fromFallback ?? fallback;
};

/**
 * Alternate `reportingMonth` string for Firestore queries when legacy docs used `YYYY-M`
 * (e.g. `2026-3`) and the UI uses `YYYY-MM` (`2026-03`), or the reverse.
 */
export const getAlternateReportingMonth = (reportingMonth: ReportingMonth): ReportingMonth | null => {
  const match = String(reportingMonth).match(/^(\d{4})-(\d{1,2})$/);
  if (!match) return null;
  const [, year, month] = match;
  if (!year || !month) return null;
  if (month.length === 2 && month.startsWith('0')) {
    return `${year}-${parseInt(month, 10)}` as ReportingMonth;
  }
  return `${year}-${month.padStart(2, '0')}` as ReportingMonth;
};

export const formatReportingMonth = (date: Date): ReportingMonth => {
  const month = String(date.getMonth() + 1).padStart(MONTH_PAD, '0');
  return `${date.getFullYear()}-${month}`;
};

/** Build `YYYY-MM` from calendar year/month without local↔Bangkok `Date` conversion. */
export const reportingMonthFromCalendarParts = (
  year: number,
  monthIndex: number,
): ReportingMonth => {
  if (!Number.isInteger(year) || !Number.isInteger(monthIndex) || monthIndex < 0 || monthIndex > 11) {
    throw new RangeError(`Invalid reporting month parts: year=${year}, monthIndex=${monthIndex}`);
  }
  return `${year}-${String(monthIndex + 1).padStart(MONTH_PAD, '0')}` as ReportingMonth;
};

export const getReportingMonth = (date: Date): ReportingMonth => {
  const { year, month } = getTimeZoneYearMonth(date, INDOCHINA_TIME_ZONE);
  return `${year}-${month}`;
};

export const getLockDate = (reportingMonth: ReportingMonth): Date => {
  const parts = parseReportingMonthParts(reportingMonth);
  if (!parts) return new Date(NaN);

  return new Date(parts.year, parts.monthIndex + 1, LOCK_DAY);
};

export const isEditableMonth = (targetMonth: ReportingMonth, now: Date = new Date()): boolean => {
  const canonicalTarget = canonicalReportingMonth(targetMonth, targetMonth);
  if (!parseReportingMonthParts(canonicalTarget)) return false;

  const currentMonth = getReportingMonth(now);
  return canonicalTarget <= currentMonth;
};

export const parseReportingMonth = (targetMonth: ReportingMonth): Date => {
  const parts = parseReportingMonthParts(targetMonth);
  if (!parts) return new Date(NaN);

  return new Date(parts.year, parts.monthIndex, 1);
};

export const getPreviousReportingMonth = (targetMonth: ReportingMonth): ReportingMonth => {
  const parts = parseReportingMonthParts(targetMonth);

  if (!parts) return targetMonth;

  return formatReportingMonth(new Date(parts.year, parts.monthIndex - 1, 1));
};

const BANGKOK_OFFSET_MS = 7 * 60 * 60 * 1000;

/** UTC instant when the Bangkok wall clock hits 00:00 on the 1st of the given calendar month. */
export const getBangkokMonthStartUtcMs = (year: number, monthIndex: number): number =>
  Date.UTC(year, monthIndex, 1, 0, 0, 0) - BANGKOK_OFFSET_MS;

/** Milliseconds until the next Bangkok reporting-month boundary (1st 00:00 Asia/Bangkok). */
export const getMsUntilNextBangkokReportingMonth = (now = new Date()): number => {
  const parts = parseReportingMonthParts(getReportingMonth(now));
  if (!parts) return 60_000;

  let nextYear = parts.year;
  let nextMonthIndex = parts.monthIndex + 1;
  if (nextMonthIndex > 11) {
    nextMonthIndex = 0;
    nextYear += 1;
  }

  const nextStart = getBangkokMonthStartUtcMs(nextYear, nextMonthIndex);
  return Math.max(1, nextStart - now.getTime());
};

export const getSelectableReportingMonths = (
  anchorYear = 2026,
  anchorMonthIndex = 0,
  endAsOf: ReportingMonth | Date = new Date(),
): ReportingMonth[] => {
  const endParts =
    endAsOf instanceof Date
      ? parseReportingMonthParts(getReportingMonth(endAsOf))
      : parseReportingMonthParts(endAsOf);
  if (!endParts) return [];

  const { year: endYear, monthIndex: endMonth } = endParts;
  const months: ReportingMonth[] = [];
  for (let y = anchorYear; y <= endYear; y += 1) {
    const startM = y === anchorYear ? anchorMonthIndex : 0;
    const endM = y === endYear ? endMonth : 11;
    for (let m = startM; m <= endM; m += 1) {
      months.push(reportingMonthFromCalendarParts(y, m));
    }
  }
  return months;
};

export const formatReportingMonthLabel = (
  reportingMonth: ReportingMonth,
  options: Intl.DateTimeFormatOptions = { month: 'long', year: 'numeric' },
): string => {
  const parts = parseReportingMonthParts(reportingMonth);

  if (!parts) return reportingMonth;

  const date = new Date(parts.year, parts.monthIndex, 1);
  return new Intl.DateTimeFormat('th-TH-u-ca-gregory', options).format(date);
};

export const formatReportingMonthLabelBuddhist = (
  reportingMonth: ReportingMonth,
  options: Intl.DateTimeFormatOptions = { month: 'long', year: 'numeric' },
): string => {
  const parts = parseReportingMonthParts(reportingMonth);

  if (!parts) return reportingMonth;

  const date = new Date(parts.year, parts.monthIndex, 1);
  return new Intl.DateTimeFormat('th-TH-u-ca-buddhist', options).format(date);
};

/** Fiscal year: Q1 = Oct–Dec of previous calendar year; Q2–Q4 = Jan–Sep of labeled year. */
export type FiscalQuarter = 1 | 2 | 3 | 4;

/**
 * Map a reporting month to fiscal year + quarter (งบประมาณ: ต.ค.–ก.ย.).
 * Any month inside a quarter maps to the same FY/Q as that quarter’s anchor month.
 */
export function reportingMonthToFiscalYearQuarter(
  reportingMonth: ReportingMonth,
): { fiscalYear: number; quarter: FiscalQuarter } {
  const canonical = canonicalReportingMonth(reportingMonth, reportingMonth);
  const match = REPORTING_MONTH_REGEX.exec(canonical);
  if (!match) return { fiscalYear: new Date().getFullYear(), quarter: 2 };

  const y = Number(match[1]);
  const m = Number(match[2]);
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) {
    return { fiscalYear: new Date().getFullYear(), quarter: 2 };
  }

  if (m >= 10) return { fiscalYear: y + 1, quarter: 1 };
  if (m <= 3) return { fiscalYear: y, quarter: 2 };
  if (m <= 6) return { fiscalYear: y, quarter: 3 };
  return { fiscalYear: y, quarter: 4 };
}

/** First calendar month of the fiscal quarter (Oct, Jan, Apr, Jul). */
export function fiscalYearQuarterToAnchorReportingMonth(
  fiscalYear: number,
  quarter: FiscalQuarter,
): ReportingMonth {
  if (quarter === 1) {
    return `${fiscalYear - 1}-10` as ReportingMonth;
  }
  const startMonth = (quarter - 2) * 3 + 1;
  return `${fiscalYear}-${String(startMonth).padStart(MONTH_PAD, '0')}` as ReportingMonth;
}

/** Normalize any month in a fiscal quarter to that quarter’s anchor (for view switching). */
export function reportingMonthToFiscalQuarterAnchor(reportingMonth: ReportingMonth): ReportingMonth {
  const { fiscalYear, quarter } = reportingMonthToFiscalYearQuarter(reportingMonth);
  return fiscalYearQuarterToAnchorReportingMonth(fiscalYear, quarter);
}

/** Buddhist fiscal year for a Gregorian fiscal year (2027 → 2570). */
export function buddhistFiscalYear(gregorianFiscalYear: number): number {
  return gregorianFiscalYear + 543;
}

/** Current ปีงบประมาณ in the Buddhist calendar. October starts the next fiscal year. */
export function currentFiscalYearBe(now: Date = new Date()): number {
  return buddhistFiscalYear(reportingMonthToFiscalYearQuarter(getReportingMonth(now)).fiscalYear);
}

const FISCAL_QUARTER_RANGE: Record<FiscalQuarter, (yearShort: string, previousShort: string) => string> = {
  1: (_yearShort, previousShort) => `ต.ค. - ธ.ค. ${previousShort}`,
  2: (yearShort) => `ม.ค. - มี.ค. ${yearShort}`,
  3: (yearShort) => `เม.ย. - มิ.ย. ${yearShort}`,
  4: (yearShort) => `ก.ค. - ก.ย. ${yearShort}`,
};

/** `1/70` for fiscal year 2570, quarter 1. `gregorianFiscalYear` is the Western fiscal year. */
export function fiscalQuarterShortLabel(gregorianFiscalYear: number, quarter: FiscalQuarter): string {
  return `${quarter}/${String(buddhistFiscalYear(gregorianFiscalYear)).slice(-2)}`;
}

/** `ต.ค. - ธ.ค. 69` for fiscal year 2570, quarter 1. */
export function fiscalQuarterRangeLabel(gregorianFiscalYear: number, quarter: FiscalQuarter): string {
  const be = buddhistFiscalYear(gregorianFiscalYear);
  const yearShort = String(be).slice(-2);
  const previousShort = String(be - 1).slice(-2);
  return FISCAL_QUARTER_RANGE[quarter](yearShort, previousShort);
}

/** `1/70 (ต.ค. - ธ.ค. 69)`. */
export function fiscalQuarterOptionLabel(gregorianFiscalYear: number, quarter: FiscalQuarter): string {
  return `${fiscalQuarterShortLabel(gregorianFiscalYear, quarter)} (${fiscalQuarterRangeLabel(gregorianFiscalYear, quarter)})`;
}

/** The three `YYYY-MM` keys for submissions in this fiscal quarter, in chronological order. */
export function getFiscalQuarterMonths(reportingMonth: ReportingMonth): ReportingMonth[] {
  const { fiscalYear, quarter } = reportingMonthToFiscalYearQuarter(reportingMonth);
  if (quarter === 1) {
    const y = fiscalYear - 1;
    return [
      `${y}-10`,
      `${y}-11`,
      `${y}-12`,
    ] as ReportingMonth[];
  }
  const y = fiscalYear;
  const start = (quarter - 2) * 3 + 1;
  return [
    `${y}-${String(start).padStart(MONTH_PAD, '0')}`,
    `${y}-${String(start + 1).padStart(MONTH_PAD, '0')}`,
    `${y}-${String(start + 2).padStart(MONTH_PAD, '0')}`,
  ] as ReportingMonth[];
}
